"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { io } from "socket.io-client";
import { FileImage, Loader2, MessageCircle, Send, ShieldCheck, X } from "lucide-react";
import { api, SOCKET_BASE } from "@/lib/api";
import { getSession } from "@/lib/auth";
import type { ChatMessage, Conversation } from "@/lib/types";

export function JobChat({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const session = getSession();
  const role = session?.role;
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [busy, setBusy] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    setMounted(true);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let socket: ReturnType<typeof io> | null = null;
    (async () => {
      try {
        const conversations = await api.getConversations() as Conversation[];
        const current = conversations.find((item) => item.jobId === jobId);
        if (!current) throw new Error("Chat becomes available after a contractor bid is accepted.");
        setConversation(current);
        const result = await api.getConversationMessages(current.id) as { conversation: Conversation; messages: ChatMessage[] };
        setConversation(result.conversation);
        setMessages(result.messages);
        await api.markConversationRead(current.id);
        socket = io(SOCKET_BASE, {
          auth: { token: session?.token },
        });
        socket.emit("join_conversation", { conversationId: current.id });
        socket.on("message:new", (message: ChatMessage) => {
          setMessages((existing) => existing.some((item) => item.id === message.id) ? existing : [...existing, message]);
          if (message.senderRole !== role) void api.markConversationRead(current.id);
        });
        socket.on("contact:updated", () => void refreshConversation(current.id));
      } catch (caught: any) {
        setError(caught.message || "Could not open chat");
      } finally {
        setBusy(false);
      }
    })();
    return () => {
      socket?.disconnect();
    };
  }, [jobId]);

  useEffect(() => {
    // Some browser extensions/polyfills override scrollIntoView to return a
    // Promise instead of undefined. An implicit-return effect here would
    // hand that value to React as its cleanup function, which crashes with
    // "X is not a function" the next time this effect re-runs or unmounts -
    // exactly matching reports of a crash on sending a message or closing
    // the chat. Wrapping in a block body guarantees no return value at all.
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function refreshConversation(id: string) {
    const result = await api.getConversationMessages(id) as { conversation: Conversation; messages: ChatMessage[] };
    setConversation(result.conversation);
  }

  async function sendMessage() {
    if (!conversation || (!text.trim() && attachments.length === 0)) return;
    setSending(true);
    try {
      const message = await api.sendConversationMessage(conversation.id, { body: text.trim(), attachments }) as ChatMessage;
      if (!isMountedRef.current) return;
      setMessages((existing) => existing.some((item) => item.id === message.id) ? existing : [...existing, message]);
      setText("");
      setAttachments([]);
    } catch (caught: any) {
      if (isMountedRef.current) setError(caught.message || "Message failed");
    } finally {
      if (isMountedRef.current) setSending(false);
    }
  }

  async function uploadAttachment(files: FileList | null) {
    if (!files?.length) return;
    setSending(true);
    try {
      const result = await api.uploadChatFiles(Array.from(files).slice(0, 5));
      if (!isMountedRef.current) return;
      setAttachments((current) => [...current, ...result.urls].slice(0, 5));
    } catch (caught: any) {
      if (isMountedRef.current) setError(caught.message || "Attachment upload failed");
    } finally {
      if (isMountedRef.current) setSending(false);
    }
  }

  async function toggleConsent() {
    if (!conversation) return;
    const ownConsent = role === "customer" ? conversation.customerContactConsent : conversation.contractorContactConsent;
    const updated = await api.updateContactConsent(conversation.id, !ownConsent) as Conversation;
    setConversation(updated);
  }

  const ownConsent = role === "customer" ? conversation?.customerContactConsent : conversation?.contractorContactConsent;

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] isolate flex items-end justify-end bg-slate-950/45 p-0 backdrop-blur-sm sm:p-6">
      <section className="relative z-10 flex h-[100dvh] w-full flex-col overflow-hidden bg-white shadow-2xl sm:h-[min(88dvh,820px)] sm:max-w-lg sm:rounded-3xl sm:border sm:border-slate-200">
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><MessageCircle /></div>
            <div><h2 className="font-black">{conversation?.participant.name || "Job chat"}</h2><p className="text-xs font-bold text-slate-400">{conversation?.job.category || "Secure conversation"}</p></div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="rounded-xl bg-slate-100 p-3 text-slate-500"><X size={18} /></button>
          </div>
        </header>

        {conversation && (
          <div className="border-b border-slate-100 bg-slate-50 px-5 py-3">
            <button onClick={toggleConsent} className="flex items-center gap-2 text-xs font-bold text-slate-600">
              <ShieldCheck size={15} className="text-emerald-600" />
              {ownConsent ? "Contact sharing enabled" : "Allow contact sharing after mutual consent"}
            </button>
            {conversation.contactDetails && <p className="mt-2 text-xs font-semibold text-emerald-700">Mutual consent active. Contact details are now available in this conversation.</p>}
          </div>
        )}

        <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50/60 p-5">
          {busy && <div className="flex h-full items-center justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>}
          {!busy && error && !conversation && <p className="rounded-2xl bg-red-50 p-4 text-sm font-bold text-red-700">{error}</p>}
          {messages.map((message) => {
            const mine = message.senderRole === role;
            return <div key={message.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}><div className={`max-w-[82%] rounded-2xl px-4 py-3 ${mine ? "bg-emerald-600 text-white" : "border border-slate-200 bg-white text-slate-800"}`}>{message.body && <p className="whitespace-pre-wrap text-sm font-medium">{message.body}</p>}{(message.attachments || []).map((url) => <a key={url} href={url} target="_blank" rel="noreferrer" className="mt-2 block text-xs font-bold underline">Open attachment</a>)}<p className={`mt-1 text-[9px] ${mine ? "text-emerald-100" : "text-slate-400"}`}>{new Date(message.createdAt).toLocaleString()}</p></div></div>;
          })}
          <div ref={bottomRef} />
        </div>

        {error && conversation && <p className="border-t border-red-100 bg-red-50 px-5 py-3 text-xs font-bold text-red-700">{error}</p>}
        <footer className="border-t border-slate-200 bg-white p-4">
          {attachments.length > 0 && <p className="mb-2 text-xs font-bold text-emerald-700">{attachments.length} attachment(s) ready</p>}
          <div className="flex items-end gap-2">
            <label className="cursor-pointer rounded-xl bg-slate-100 p-3 text-slate-500"><FileImage size={19} /><input type="file" accept="image/*,.pdf" multiple className="hidden" onChange={(event) => uploadAttachment(event.target.files)} /></label>
            <textarea value={text} onChange={(event) => setText(event.target.value)} placeholder="Write a message..." rows={1} className="max-h-28 min-h-12 flex-1 resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-600" />
            <button onClick={sendMessage} disabled={sending || !conversation || conversation.status !== "active"} className="rounded-xl bg-emerald-600 p-3 text-white disabled:opacity-40">{sending ? <Loader2 size={19} className="animate-spin" /> : <Send size={19} />}</button>
          </div>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
